import { useCallback, useEffect, useRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { api } from "../api/client.js";
import { resolveProductMediaUrl } from "../data/products.js";
import {
  formatMinorAmount,
  stripeConfigured,
  stripePromise,
} from "../config/stripe.js";
import {
  getApiFieldErrors,
  getRequestErrorMessage,
} from "../Utils/formValidation.js";
import { validatePurchaseDetails } from "../Utils/purchaseValidation.js";
import MockUpsShippingStep from "./MockUpsShippingStep.js";
import StripePaymentForm from "./StripePaymentForm.js";
import "../Style/PurchaseModal.css";

const INITIAL_CUSTOMER = { fullName: "", email: "", phone: "" };
const INITIAL_BILLING = {
  country: "US",
  state: "",
  city: "",
  zip: "",
  line1: "",
  line2: "",
};
const EMPTY_CANDLE_IDS = [];
const BALLOON_MAX_QUANTITY = 15;
const CANDLE_MAX_QUANTITY = 200;
const DEFAULT_MAX_QUANTITY = 200;

const getMaxQuantity = (product, itemType) => {
  if (itemType === "candle") return CANDLE_MAX_QUANTITY;
  return product?.productType === "balloon"
    ? BALLOON_MAX_QUANTITY
    : DEFAULT_MAX_QUANTITY;
};

const clampQuantity = (value, maxQuantity) => {
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return 1;
  return Math.min(maxQuantity, Math.max(1, Math.trunc(quantity)));
};

function PurchaseField({ label, name, value, onChange, error, ...inputProps }) {
  const errorId = `purchase-${name.replace(".", "-")}-error`;
  return (
    <label className="purchase-field">
      <span>{label}</span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        {...inputProps}
      />
      {error && (
        <small id={errorId} className="form-field-error">
          {error}
        </small>
      )}
    </label>
  );
}

export default function PurchaseModal({
  product,
  itemType = "product",
  candleIds = EMPTY_CANDLE_IDS,
  returnPath,
  onClose,
}) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const paymentBusyRef = useRef(false);
  const [step, setStep] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [customer, setCustomer] = useState(INITIAL_CUSTOMER);
  const [billingAddress, setBillingAddress] = useState(INITIAL_BILLING);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [initializing, setInitializing] = useState(false);
  const [payment, setPayment] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const maxQuantity = getMaxQuantity(product, itemType);
  const [paymentBusy, setPaymentBusy] = useState(false);

  const requestClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    closeRef.current = requestClose;
  }, [requestClose]);

  useEffect(() => {
    paymentBusyRef.current = paymentBusy;
  }, [paymentBusy]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !paymentBusyRef.current) {
        closeRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setQuoteLoading(true);
    setError("");

    api.payments
      .quote(product.id, quantity, {
        signal: controller.signal,
        itemType,
        candleIds,
      })
      .then((response) => setQuote(response.data))
      .catch((requestError) => {
        if (requestError.name !== "AbortError") {
          setQuote(null);
          setError(
            getRequestErrorMessage(
              requestError,
              "The order total could not be loaded. Please try again.",
            ),
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setQuoteLoading(false);
      });

    return () => controller.abort();
  }, [candleIds, itemType, product.id, quantity]);

  const handleDetailsChange = ({ target: { name, value } }) => {
    const [group, field] = name.split(".");
    if (group === "customer") {
      setCustomer((current) => ({ ...current, [field]: value }));
    } else {
      setBillingAddress((current) => ({ ...current, [field]: value }));
    }
    setFieldErrors((current) => ({ ...current, [name]: "" }));
    setError("");
  };

  const applySuggestedAddress = (address) => {
    setBillingAddress((current) => ({ ...current, ...address }));
    setFieldErrors((current) => ({
      ...current,
      "billingAddress.country": "",
      "billingAddress.state": "",
      "billingAddress.city": "",
      "billingAddress.zip": "",
      "billingAddress.line1": "",
      "billingAddress.line2": "",
    }));
    setError("");
  };

  const continueToPayment = async (event) => {
    event.preventDefault();
    if (initializing) return;

    const validationErrors = validatePurchaseDetails(customer, billingAddress);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setError("Please correct the highlighted billing details.");
      return;
    }
    if (!stripeConfigured) {
      setError("Payments are not configured for this website.");
      return;
    }
    if (!selectedShipping?.serviceCode) {
      setError("Please choose a UPS delivery option.");
      return;
    }

    setInitializing(true);
    setError("");
    try {
      const response = await api.payments.createIntent({
        itemType,
        ...(itemType === "candle" ? { candleIds } : { productId: product.id }),
        quantity,
        customer: {
          fullName: customer.fullName.trim(),
          email: customer.email.trim(),
          phone: customer.phone.trim(),
        },
        billingAddress: {
          country: billingAddress.country.trim().toUpperCase(),
          state: billingAddress.state.trim(),
          city: billingAddress.city.trim(),
          zip: billingAddress.zip.trim(),
          line1: billingAddress.line1.trim(),
          line2: billingAddress.line2.trim(),
        },
        shipping: {
          address: {
            country: billingAddress.country.trim().toUpperCase(),
            state: billingAddress.state.trim(),
            city: billingAddress.city.trim(),
            zip: billingAddress.zip.trim(),
            line1: billingAddress.line1.trim(),
            line2: billingAddress.line2.trim(),
            residential: true,
          },
          selectedRate: selectedShipping,
        },
      });
      setPayment(response.data);
      setQuote({
        ...quote,
        totalAmount: response.data.amount,
        currency: response.data.currency,
      });
      setStep(3);
    } catch (requestError) {
      const apiErrors = getApiFieldErrors(requestError);
      setFieldErrors(apiErrors);
      setError(
        Object.keys(apiErrors).length
          ? "Please correct the highlighted billing details."
          : getRequestErrorMessage(
              requestError,
              "Payment could not be initialized. Please try again.",
            ),
      );
    } finally {
      setInitializing(false);
    }
  };

  const canClose = !paymentBusy;
  const shippingItems =
    itemType === "candle"
      ? candleIds.map((candleId) => ({ itemType: "candle", candleId, quantity }))
      : [{ itemType: "product", productId: product.id, quantity }];
  const title =
    result?.status === "succeeded"
      ? "Payment successful"
      : result?.status === "processing"
        ? "Payment processing"
        : step === 1
          ? "Review your order"
          : step === 2
            ? "Shipping information"
            : "Secure payment";

  return (
    <div
      className="purchase-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && canClose) requestClose();
      }}
    >
      <section
        className="purchase-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="purchase-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="purchase-header">
          <div>
            <p>Up &amp; Away </p>
            <h2 id="purchase-title">{title}</h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={!canClose}
            aria-label="Close purchase"
          >
            ×
          </button>
        </header>

        {!result && (
          <ol className="purchase-steps" aria-label="Purchase progress">
            {["Order", "Shipping", "Payment"].map((label, index) => (
              <li className={step >= index + 1 ? "active" : ""} key={label}>
                <span>{index + 1}</span>
                {label}
              </li>
            ))}
          </ol>
        )}

        {result ? (
          <div className={`purchase-result is-${result.status}`} role="status">
            <span aria-hidden="true">
              {result.status === "succeeded" ? "✓" : "◷"}
            </span>
            <h3>
              {result.status === "succeeded"
                ? "Your order is confirmed."
                : "Your payment is processing."}
            </h3>
            <p>
              {result.status === "succeeded"
                ? `Thank you, ${customer.fullName}. Your order reference is ${result.order.id}.`
                : "We will update the order automatically when Stripe confirms the payment."}
            </p>
            <button
              type="button"
              className="purchase-primary"
              onClick={requestClose}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {step === 1 && (
              <div className="purchase-review">
                <div className="purchase-product">
                  {product.imageUrl || product.image ? (
                    <img
                      src={resolveProductMediaUrl(
                        product.imageUrl || product.image,
                      )}
                      alt={product.alt}
                    />
                  ) : (
                    <span
                      className="purchase-candle-preview"
                      aria-hidden="true"
                    >
                      {(product.candles || [product])
                        .slice(0, 4)
                        .map((candle) => (
                          <i
                            key={candle.id}
                            style={{ backgroundColor: candle.color?.value }}
                          />
                        ))}
                    </span>
                  )}
                  <div>
                    <p>
                      {itemType === "candle"
                        ? "Candle request"
                        : "The Collection"}
                    </p>
                    <h3>{product.name}</h3>
                    <span>{product.mood}</span>
                  </div>
                </div>

                <div className="purchase-quantity-row">
                  <span>Quantity</span>
                  <div className="purchase-quantity-stack">
                    <div className="purchase-quantity">
                      <button
                        type="button"
                        onClick={() =>
                          setQuantity((current) => Math.max(1, current - 1))
                        }
                        disabled={quantity === 1}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min="1"
                        max={maxQuantity}
                        inputMode="numeric"
                        value={quantity}
                        onChange={({ target }) =>
                          setQuantity(clampQuantity(target.value, maxQuantity))
                        }
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setQuantity((current) =>
                            Math.min(maxQuantity, current + 1),
                          );
                        }}
                        disabled={quantity === maxQuantity}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                    {quantity === maxQuantity && (
                      <small className="quantity-limit-note">
                        For greater quantities, contact us.
                      </small>
                    )}
                  </div>
                </div>

                <div className="purchase-total">
                  <span>Total</span>
                  <strong>
                    {quoteLoading
                      ? "Calculating…"
                      : quote
                        ? formatMinorAmount(quote.totalAmount, quote.currency)
                        : "Unavailable"}
                  </strong>
                </div>

                {error && (
                  <p className="purchase-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  className="purchase-primary"
                  onClick={() => {
                    setError("");
                    setStep(2);
                  }}
                  disabled={quoteLoading || !quote}
                >
                  Continue
                </button>
              </div>
            )}

            {step === 2 && (
              <MockUpsShippingStep
                customer={customer}
                billingAddress={billingAddress}
                fieldErrors={fieldErrors}
                error={error}
                initializing={initializing}
                productAmount={quote?.totalAmount || 0}
                currency={quote?.currency || "USD"}
                items={shippingItems}
                onChange={handleDetailsChange}
                onApplySuggestedAddress={applySuggestedAddress}
                onShippingSelected={setSelectedShipping}
                onBack={() => {
                  setError("");
                  setSelectedShipping(null);
                  setStep(1);
                }}
                onSubmit={continueToPayment}
              />
            )}

            {false && step === 2 && (
              <form
                className="purchase-details-form"
                onSubmit={continueToPayment}
                noValidate
              >
                <div className="purchase-field-grid">
                  <PurchaseField
                    label="Full name *"
                    name="customer.fullName"
                    value={customer.fullName}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.fullName"]}
                    autoComplete="name"
                  />
                  <PurchaseField
                    label="Email *"
                    name="customer.email"
                    value={customer.email}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.email"]}
                    type="email"
                    autoComplete="email"
                  />
                  <PurchaseField
                    label="Phone *"
                    name="customer.phone"
                    value={customer.phone}
                    onChange={handleDetailsChange}
                    error={fieldErrors["customer.phone"]}
                    type="tel"
                    autoComplete="tel"
                  />
                  <PurchaseField
                    label="Country code *"
                    name="billingAddress.country"
                    value={billingAddress.country}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.country"]}
                    placeholder="LB"
                    maxLength={2}
                    autoComplete="country"
                  />
                  <PurchaseField
                    label="State / Province *"
                    name="billingAddress.state"
                    value={billingAddress.state}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.state"]}
                    autoComplete="address-level1"
                  />
                  <PurchaseField
                    label="City *"
                    name="billingAddress.city"
                    value={billingAddress.city}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.city"]}
                    autoComplete="address-level2"
                  />
                  <PurchaseField
                    label="ZIP / Postal code *"
                    name="billingAddress.zip"
                    value={billingAddress.zip}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.zip"]}
                    autoComplete="postal-code"
                  />
                  <PurchaseField
                    label="Address line 1 *"
                    name="billingAddress.line1"
                    value={billingAddress.line1}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.line1"]}
                    autoComplete="address-line1"
                  />
                  <PurchaseField
                    label="Address line 2"
                    name="billingAddress.line2"
                    value={billingAddress.line2}
                    onChange={handleDetailsChange}
                    error={fieldErrors["billingAddress.line2"]}
                    autoComplete="address-line2"
                  />
                </div>

                {error && (
                  <p className="purchase-error" role="alert">
                    {error}
                  </p>
                )}

                <div className="purchase-actions">
                  <button
                    type="button"
                    className="purchase-secondary"
                    onClick={() => {
                      setError("");
                      setStep(1);
                    }}
                    disabled={initializing}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="purchase-primary"
                    disabled={initializing}
                  >
                    {initializing
                      ? "Preparing secure payment…"
                      : "Continue to payment"}
                  </button>
                </div>
              </form>
            )}

            {step === 3 && payment?.clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret: payment.clientSecret,
                  appearance: {
                    theme: "stripe",
                    variables: {
                      colorPrimary: "#a8873a",
                      colorText: "#1c1913",
                      colorBackground: "#fffefa",
                      borderRadius: "0px",
                      fontFamily: "Lora, serif",
                    },
                  },
                }}
              >
                <StripePaymentForm
                  product={product}
                  customer={customer}
                  billingAddress={billingAddress}
                  clientSecret={payment.clientSecret}
                  paymentIntentId={payment.paymentIntentId}
                  amount={payment.amount}
                  currency={payment.currency}
                  onBusyChange={setPaymentBusy}
                  returnPath={returnPath}
                  onSuccess={(order) =>
                    setResult({ status: "succeeded", order })
                  }
                  onProcessing={(order) =>
                    setResult({ status: "processing", order })
                  }
                />
              </Elements>
            )}
          </>
        )}
      </section>
    </div>
  );
}
